import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { Toaster } from 'react-hot-toast';
import { AuthProvider } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import ProtectedRoute from './components/ProtectedRoute';
import PublicRoute from './components/PublicRoute';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import RemindersPage from './pages/RemindersPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UsersPage from './pages/UsersPage';
import SystemSettingsPage from './pages/SystemSettingsPage';
import LeadsPage from './pages/LeadsPage';
import ClientsPage from './pages/ClientsPage';
import ClientManagementPage from './pages/ClientManagementPage';
import UnauthorizedPage from './pages/UnauthorizedPage';
import SigningPage from './pages/SigningPage';
import PublicFolderUploadPage from './pages/PublicFolderUploadPage';
import ReportsPage from './pages/ReportsPage';
import PaymentsManagementPage from './pages/PaymentsManagementPage';
import AppointmentConfirmationPage from './pages/AppointmentConfirmationPage';
import CardcomResult from './pages/billing/CardcomResult';

const AppContainer = styled.div`
  min-height: 100vh;
  background-color: white;
  display: flex;
`;

const MainContent = styled.main`
  flex: 1;
  transition: margin-right 0.3s ease;
  
  @media (max-width: 1023px) {
    margin-right: 0;
  }
`;

function App() {
  return (
    <AuthProvider>
      <Router>
        <Toaster
          position="top-center"
          reverseOrder={false}
          toastOptions={{
            duration: 4000,
            style: {
              direction: 'rtl',
              background: 'var(--surface-color)',
              color: 'var(--text-primary)',
            },
            success: {
              iconTheme: {
                primary: 'var(--success-color)',
                secondary: 'white',
              },
            },
            error: {
              iconTheme: {
                primary: 'var(--error-color)',
                secondary: 'white',
              },
            },
          }}
        />
        <Routes>
          {/* Public routes */}
          <Route 
            path="/login" 
            element={
              <PublicRoute>
                <LoginPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/forgot-password" 
            element={
              <PublicRoute>
                <ForgotPasswordPage />
              </PublicRoute>
            } 
          />
          <Route 
            path="/reset-password/:token" 
            element={
              <PublicRoute>
                <ResetPasswordPage />
              </PublicRoute>
            } 
          />
          
          {/* Public signing route - no authentication required */}
          <Route
            path="/sign/:documentSetId"
            element={<SigningPage />}
          />

          {/* Public folder document upload route - no authentication required */}
          <Route
            path="/folder-upload/:publicId"
            element={<PublicFolderUploadPage />}
          />

          {/* Public appointment confirmation route - no authentication required */}
          <Route
            path="/appointment-confirmation/:token"
            element={<AppointmentConfirmationPage />}
          />

          {/* Cardcom redirect target - loaded inside iframe after card capture, breaks out to top window */}
          <Route
            path="/billing/cardcom-result"
            element={<CardcomResult />}
          />
          
          {/* Unauthorized route - accessible to authenticated users but shows access denied */}
          <Route 
            path="/unauthorized" 
            element={
              <ProtectedRoute>
                <UnauthorizedPage />
              </ProtectedRoute>
            } 
          />

          {/* Protected routes with navigation */}
          <Route 
            path="/*" 
            element={
              <ProtectedRoute>
                <AppContainer>
                  <Navigation />
                  <MainContent>
                    <Routes>
                      <Route path="/" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/reminders" element={<RemindersPage />} />

                      {/* Lead Management — SystemAdmin, SalesRep, Secretary */}
                      <Route
                        path="/leads"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 2, 4]}>
                            <LeadsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Client Management — SystemAdmin, CaseManager, Secretary */}
                      <Route
                        path="/clients"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 3, 4]}>
                            <ClientsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Individual Client Management — SystemAdmin, CaseManager, Secretary */}
                      <Route
                        path="/clients/:clientId/manage/documents/:folderId"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 3, 4]}>
                            <ClientManagementPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/clients/:clientId/manage/:tab?"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 3, 4]}>
                            <ClientManagementPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* User Management - SystemAdmin */}
                      <Route
                        path="/users"
                        element={
                          <ProtectedRoute requiredPermissionLevel={1}>
                            <UsersPage />
                          </ProtectedRoute>
                        }
                      />
                      
                      {/* System Settings - System Admins only */}
                      <Route
                        path="/system-settings"
                        element={
                          <ProtectedRoute requiredPermissionLevel={1}>
                            <SystemSettingsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Reports — SystemAdmin (full) and Secretary (filtered to her scope) */}
                      <Route
                        path="/reports"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 4]}>
                            <ReportsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Payments Management — SystemAdmin + Secretary */}
                      <Route
                        path="/payments-management"
                        element={
                          <ProtectedRoute requiredPermissionLevels={[1, 4]}>
                            <PaymentsManagementPage />
                          </ProtectedRoute>
                        }
                      />
                    </Routes>
                  </MainContent>
                </AppContainer>
              </ProtectedRoute>
            } 
          />
          </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;